// Single source of truth for project + enquiry data.
//
// The public site (ProjectsPage / ProjectDetailPage) reads from here, and the
// /admin backpanel writes to here. Everything is persisted to localStorage so
// it survives reloads without needing a backend. To go live for ALL visitors,
// edits would need to be exported and committed (see admin "Export" button) —
// localStorage is per-browser only.

const PROJECTS_KEY = "nr_admin_projects_v16"; // v16: corrected precise GPS coordinates for all 4 projects (Mangaon, Shrivardhan, Seaview/Dapoli, Shirdi), used by the Projects page map embed
const ENQUIRIES_KEY = "nr_admin_enquiries_v1";

// ---- Accent presets (so the admin doesn't have to write CSS gradients) ----
export const ACCENTS = {
  amber: {
    label: "Amber",
    card: "linear-gradient(135deg, rgba(212, 164, 92,0.35) 0%, rgba(160,120,50,0.2) 40%, rgba(10,13,24,0.9) 100%)",
    detail: "linear-gradient(135deg, rgba(212, 164, 92,0.28) 0%, rgba(180,130,60,0.15) 30%, rgba(10,13,24,0) 70%)",
  },
  blue: {
    label: "Blue",
    card: "linear-gradient(135deg, rgba(80,160,220,0.35) 0%, rgba(40,100,180,0.2) 40%, rgba(10,13,24,0.9) 100%)",
    detail: "linear-gradient(135deg, rgba(80,160,220,0.28) 0%, rgba(40,100,180,0.15) 30%, rgba(10,13,24,0) 70%)",
  },
  gold: {
    label: "Gold",
    card: "linear-gradient(135deg, rgba(220,160,60,0.35) 0%, rgba(180,110,30,0.2) 40%, rgba(10,13,24,0.9) 100%)",
    detail: "linear-gradient(135deg, rgba(220,160,60,0.28) 0%, rgba(200,120,40,0.15) 30%, rgba(10,13,24,0) 70%)",
  },
  green: {
    label: "Green",
    card: "linear-gradient(135deg, rgba(26,140,78,0.35) 0%, rgba(15,110,59,0.2) 40%, rgba(10,13,24,0.9) 100%)",
    detail: "linear-gradient(135deg, rgba(26,140,78,0.28) 0%, rgba(15,110,59,0.15) 30%, rgba(10,13,24,0) 70%)",
  },
};

export const STATUS_OPTIONS = [
  { key: "build", label: "Under Construction" },
  { key: "ready", label: "Ready Possession" },
];

// ---- External project pages ----------------------------------------------
// The detailed project microsites already live on the main V7Vista site. This
// marketing site is a teaser: any "explore / more info" action routes out to
// the corresponding live page rather than an internal detail route.
export const PROJECT_EXTERNAL_URL = {
  "nausiddhi-residency":      "https://v7vista.com/projects/nausiddhi-residence", // Mangaon
  "blue-rain-resort-homes":   "https://v7vista.com/projects/blue-rain",           // Shrivardhan
  "sai-shrusti":              "https://v7vista.com/projects/sai-shrushti",        // Shirdi
  "nausiddhi-coastal-greens": "https://v7vista.com/projects/seaview-enclave",     // Dapoli · Seaview Enclaves
};
// Fallback to the V7Vista projects index if a project isn't mapped yet.
export function projectExternalUrl(idOrProject) {
  const id = typeof idOrProject === "string" ? idOrProject : idOrProject?.id;
  return (idOrProject && idOrProject.externalUrl) || PROJECT_EXTERNAL_URL[id] || "https://v7vista.com/projects";
}

// ---- Seed data: the 3 projects that ship with the site today ----
const SEED_PROJECTS = [
  {
    id: "nausiddhi-residency",
    name: "Nausiddhi Residence",
    location: "Mangaon · Raigad",
    statusKey: "build",
    typeKey: "plot",
    type: "Residential plots",
    accentKey: "amber",
    area: "207 Acres",
    units: "3,600+",
    plotsTotal: 3600,
    plotSize: "1,000 – 1,500 sq ft",
    starting: "On request",
    constructionNote: "Compound wall work started",
    coordinates: "18.247978° N, 73.324864° E",
    mapQuery: "Mangaon,Raigad,Maharashtra",
    amenities: "Wall Compound, Internal Roads, Water Supply, Drainage & Sewage, Electricity, Street Lights, Lavish Entrance, CCTV, Landscaping, Gated Security",
    features: "3 Club Houses, 3 Swimming Pools, Jacuzzi & Rejuvenation Hub, Cricket Ground with Mini Stadium, Football Turf, Badminton / Volleyball / Basketball Courts, Indoor Games, Open Gym, Jogging Track, Nana-Nani Park, Children's Play Area, Banquet Hall, Township Restaurant, 2-Acre Reserved International School",
    video: "",   // optional walkthrough video (path in /public, e.g. /assets/video/x.mp4)
    // Branded connectivity/proximity infographic — distances to nearby
    // landmarks, highways, beaches etc. Shown as a 3rd "Connectivity" tab
    // alongside Infrastructure/Lifestyle. Empty string hides that tab.
    connectivityImage: "/img/NausiddhiResidence_Map.png",
  },
  {
    id: "blue-rain-resort-homes",
    name: "BlueRain Resort Homes",
    location: "Ranawali · Shrivardhan",
    statusKey: "build",
    typeKey: "plot",
    type: "Residential plots",
    accentKey: "blue",
    area: "99 Acres",
    units: "900+",
    plotsTotal: 900,
    plotSize: "1,600 – 5,000 sq ft",
    starting: "On request",
    constructionNote: "Road & Compound wall work started",
    coordinates: "18.084085° N, 73.057023° E",
    mapQuery: "Ranawali,Shrivardhan,Maharashtra",
    amenities: "Wall Compound, Internal Roads, Round-the-clock Water Supply, Electricity, Street Lights, Lavish Entrance, CCTV, 24×7 Security, Landscaping",
    features: "Swimming Pool, Club House, Fine Dining, Jogging Track, Indoor Games, Outdoor Sports, Children's Play Area, Ayurveda & Spa Retreat, Adventure Trails, Mango / Cashew / Coconut Plantations, 1,000+ Trees Planted",
    video: "",   // optional walkthrough video (path in /public, e.g. /assets/video/x.mp4)
    connectivityImage: "/img/Bluerain_map.png",
  },
  {
    id: "nausiddhi-coastal-greens",
    name: "Seaview Enclave",
    location: "Dapoli · Ratnagiri",
    statusKey: "build",
    typeKey: "plot",
    accentKey: "green",
    type: "Residential plots",
    area: "63 Acres",
    // PPT states 168 NA plots (2,050–5,000 sq ft) across 3 phases —
    // plots + sea-facing studios + Bali villas.
    units: "168+",
    plotsTotal: 168,
    plotSize: "2,050 – 5,000 sq ft",
    starting: "On request",
    constructionNote: "Land development & compound wall in progress",
    coordinates: "17.617472° N, 73.142611° E",
    mapQuery: "Dapoli,Ratnagiri,Maharashtra",
    amenities: "Boundary Wall, Internal Roads, Water Supply, Electricity, Street Lights, Gated Entrance, 24×7 CCTV Security, EV Charging Station, Landscape Garden, Visitors Parking",
    features: "168+ NA Plots, Sea-Facing Studio Apartments, Bali-Themed Royal Villas, Infinity Pool + Secondary Pool, Dual Clubhouses, Iconic Bali Swing, Adventure Park, Spa & Coastal Jacuzzi, Open-Air Amphitheatre, Destination Wedding Venue, Multi-Sports Turf, Kids & Senior Citizen Park, Restaurant & Café",
    video: "",   // optional walkthrough video (path in /public, e.g. /assets/video/x.mp4)
    connectivityImage: "/img/SeaviewEnclave_Map.png",
  },
  {
    id: "sai-shrusti",
    name: "Sai Shrushti",
    location: "Dauch Khurd · Shirdi",
    statusKey: "ready",
    typeKey: "plot",
    type: "Residential plots",
    accentKey: "gold",
    area: "5.5 Acres",
    units: "117",
    plotsTotal: 117,
    plotSize: "50 – 209 sqm",
    starting: "On request",
    constructionNote: "Site Complete - Ready Possession",
    coordinates: "19.856656° N, 74.446898° E",
    mapQuery: "Dauch+Khurd,Shirdi,Maharashtra",
    amenities: "Wall Compound, Internal Roads, Water Supply, Street Lights, Landscaping, Open Gym",
    features: "",
    video: "",   // optional walkthrough video (path in /public, e.g. /assets/video/x.mp4)
    // No connectivity infographic supplied yet for this project — the tab
    // stays hidden (see ProjectAmenities) rather than showing a placeholder.
    connectivityImage: "",
  },
];

// ---- Helpers ----
function read(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function write(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    // Notify listeners in the same tab (the native "storage" event only fires
    // in OTHER tabs). Admin + public pages can subscribe to stay in sync.
    window.dispatchEvent(new CustomEvent("nr-store-change", { detail: { key } }));
  } catch {
    /* storage full or unavailable — fail silently */
  }
}

export function slugify(text) {
  return String(text)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function statusLabel(statusKey) {
  return STATUS_OPTIONS.find((s) => s.key === statusKey)?.label || statusKey;
}

// Turn "A, B, C" into [{ label }] for the detail page grids.
// Splits on commas EXCEPT ones sitting inside a number's digit-grouping
// (e.g. "1,000+ Trees Planted" must stay one item, not "1" + "000+ Trees...").
export function splitList(text) {
  return String(text || "")
    .split(/,(?!\d{3}(?:\D|$))/)
    .map((s) => s.trim())
    .filter((s) => s && s !== "-");
}

export function accentCard(accentKey) {
  return (ACCENTS[accentKey] || ACCENTS.amber).card;
}
export function accentDetail(accentKey) {
  return (ACCENTS[accentKey] || ACCENTS.amber).detail;
}

// ---- Projects API ----
export function getProjects() {
  const stored = read(PROJECTS_KEY, null);
  if (!stored) {
    write(PROJECTS_KEY, SEED_PROJECTS);
    return SEED_PROJECTS;
  }
  // Migration safety: if a future seed adds a project, surface it for
  // browsers that already have a stored list (admin edits are preserved;
  // missing seed entries are appended once).
  const missing = SEED_PROJECTS.filter((s) => !stored.some((p) => p.id === s.id));
  if (missing.length) {
    const next = [...stored, ...missing];
    write(PROJECTS_KEY, next);
    return next;
  }
  return stored;
}

export function getProject(id) {
  return getProjects().find((p) => p.id === id) || null;
}

export function saveProject(project) {
  const projects = getProjects();
  const idx = projects.findIndex((p) => p.id === project.id);
  let next;
  if (idx >= 0) {
    next = projects.map((p) => (p.id === project.id ? project : p));
  } else {
    next = [...projects, project];
  }
  write(PROJECTS_KEY, next);
  return next;
}

export function deleteProject(id) {
  const next = getProjects().filter((p) => p.id !== id);
  write(PROJECTS_KEY, next);
  return next;
}

export function resetProjects() {
  write(PROJECTS_KEY, SEED_PROJECTS);
  return SEED_PROJECTS;
}

export function emptyProject() {
  return {
    id: "",
    name: "",
    location: "",
    statusKey: "build",
    typeKey: "plot",
    type: "Residential plots",
    accentKey: "amber",
    area: "",
    units: "",
    plotsTotal: 0,
    plotSize: "",
    starting: "On request",
    constructionNote: "",
    coordinates: "",
    mapQuery: "",
    amenities: "",
    features: "",
    video: "",   // optional walkthrough video (path in /public, e.g. /assets/video/x.mp4)
  };
}

// ---- Enquiries API (contact form submissions) ----
export function getEnquiries() {
  return read(ENQUIRIES_KEY, []);
}

export function addEnquiry(enquiry) {
  const entry = {
    id: `enq_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    createdAt: new Date().toISOString(),
    read: false,
    ...enquiry,
  };
  write(ENQUIRIES_KEY, [entry, ...getEnquiries()]);
  return entry;
}

export function markEnquiryRead(id, read = true) {
  const next = getEnquiries().map((e) => (e.id === id ? { ...e, read } : e));
  write(ENQUIRIES_KEY, next);
  return next;
}

export function deleteEnquiry(id) {
  const next = getEnquiries().filter((e) => e.id !== id);
  write(ENQUIRIES_KEY, next);
  return next;
}

// Subscribe to store changes (same tab + cross tab). Returns an unsubscribe fn.
export function subscribe(callback) {
  const local = () => callback();
  const cross = (e) => {
    if (e.key === PROJECTS_KEY || e.key === ENQUIRIES_KEY) callback();
  };
  window.addEventListener("nr-store-change", local);
  window.addEventListener("storage", cross);
  return () => {
    window.removeEventListener("nr-store-change", local);
    window.removeEventListener("storage", cross);
  };
}
